import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Button, FormControl,
  InputLabel, Select, MenuItem, TextField, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, IconButton,
  Grid, Paper, Divider, Switch, FormControlLabel, Tabs, Tab
} from '@mui/material';
import {
  People, Settings, Security, Assessment, Download, Upload,
  Add, Edit, Delete, Visibility, Refresh, Warning, CheckCircle,
  Error, AdminPanelSettings, PersonAdd, History, SystemUpdate
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

const ADMIN_LOAD_TIMEOUT_MS = 30000;

function adminApi(path: string): string {
  const base = getApiUrl().replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

interface SystemOverview {
  users: {
    total: number;
    active: number;
    admins: number;
    staff: number;
  };
  customers: {
    total: number;
    active: number;
    inactive: number;
  };
  alerts: {
    total: number;
    active: number;
    recent: number;
  };
  checkins: {
    total: number;
    successful: number;
    success_rate: number;
    recent: number;
  };
  caregivers: {
    total: number;
    pending_invitations: number;
  };
  payments: {
    total_subscriptions: number;
    active_subscriptions: number;
  };
}

interface SystemHealth {
  uptime_hours: number;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent: number;
  active_connections: number;
  database_size_mb: number;
  last_backup: string | null;
  system_status: string;
}

interface User {
  id: number;
  email: string;
  full_name: string;
  phone_number?: string | null;
  role: string;
  is_active: boolean;
  assigned_customer_count?: number;
  is_orphaned?: boolean;
  created_at: string;
  updated_at: string;
}

interface UserActivity {
  user_id: number;
  email: string;
  full_name: string;
  last_login: string | null;
  login_count: number;
  last_activity: string | null;
  is_active: boolean;
  role: string;
}

interface AuditLog {
  id: string;
  user_id: number | null;
  user_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  details: any;
  ip_address: string | null;
  user_agent: string | null;
  timestamp: string;
  success: boolean;
}

interface SystemConfiguration {
  database: {
    url: string | null;
    type: string;
  };
  security: {
    access_token_expire_minutes: number;
    algorithm: string;
    secret_key_configured: boolean;
  };
  plivo: {
    auth_id_configured: boolean;
    auth_token_configured: boolean;
    phone_number_configured: boolean;
  };
  stripe: {
    secret_key_configured: boolean;
    publishable_key_configured: boolean;
  };
  logging: {
    level: string;
    log_file: string;
  };
}

const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // System Overview
  const [systemOverview, setSystemOverview] = useState<SystemOverview | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [systemConfig, setSystemConfig] = useState<SystemConfiguration | null>(null);
  
  // User Management
  const [users, setUsers] = useState<User[]>([]);
  const [orphanedUsers, setOrphanedUsers] = useState<User[]>([]);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'staff',
    is_active: true
  });
  const [purgingOrphanId, setPurgingOrphanId] = useState<number | null>(null);
  // Audit Logs
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditFilters, setAuditFilters] = useState({
    user_id: '',
    action: '',
    resource_type: '',
    start_date: '',
    end_date: ''
  });
  
  // Data Import/Export
  const [importData, setImportData] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      const runFetches = Promise.all([
        fetchSystemOverview(),
        fetchSystemHealth(),
        fetchSystemConfiguration(),
        fetchUsers(),
        fetchOrphanedUsers(),
        fetchUserActivity(),
        fetchAuditLogs(),
      ]);
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new globalThis.Error('Admin dashboard data took too long to load')),
          ADMIN_LOAD_TIMEOUT_MS
        );
      });
      try {
        await Promise.race([runFetches, timeout]);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof globalThis.Error ? e.message : 'Failed to load admin data';
          setError(msg);
          console.error('Admin dashboard load error:', e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // fetch* helpers close over latest token via re-run when token changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSystemOverview = async () => {
    try {
      const response = await fetch(adminApi('/admin/system-overview'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemOverview(data);
      }
    } catch (err) {
      console.error('Error fetching system overview:', err);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await fetch(adminApi('/admin/system-health'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemHealth(data);
      }
    } catch (err) {
      console.error('Error fetching system health:', err);
    }
  };

  const fetchSystemConfiguration = async () => {
    try {
      const response = await fetch(adminApi('/admin/system-configuration'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemConfig(data);
      }
    } catch (err) {
      console.error('Error fetching system configuration:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${adminApi('/admin/users')}?active_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const activeUsers = data.filter((user: User) => user.is_active !== false);
        setUsers(activeUsers);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchOrphanedUsers = async () => {
    try {
      const response = await fetch(adminApi('/admin/orphaned-users'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrphanedUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching orphaned users:', err);
    }
  };

  const purgeOrphanedUser = async (userId: number, email: string) => {
    if (
      !window.confirm(
        `Permanently delete incomplete signup for ${email}? This frees the email/phone for a new signup.`
      )
    ) {
      return;
    }

    try {
      setPurgingOrphanId(userId);
      const response = await fetch(adminApi(`/admin/orphaned-users/${userId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await Promise.all([fetchOrphanedUsers(), fetchUsers()]);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete orphaned user');
      }
    } catch (err) {
      setError('Failed to delete orphaned user');
    } finally {
      setPurgingOrphanId(null);
    }
  };

  const fetchUserActivity = async () => {
    try {
      const response = await fetch(adminApi('/admin/user-activity'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserActivity(data);
      }
    } catch (err) {
      console.error('Error fetching user activity:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilters.user_id) params.append('user_id', auditFilters.user_id);
      if (auditFilters.action) params.append('action', auditFilters.action);
      if (auditFilters.resource_type) params.append('resource_type', auditFilters.resource_type);
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);

      const response = await fetch(`${adminApi('/admin/audit-logs')}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  };

  const createUser = async () => {
    try {
      const response = await fetch(adminApi('/admin/users'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userForm)
      });

      if (response.ok) {
        setUserDialogOpen(false);
        setUserForm({ email: '', password: '', full_name: '', role: 'staff', is_active: true });
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create user');
      }
    } catch (err) {
      setError('Failed to create user');
    }
  };

  const updateUser = async (userId: number, updates: Partial<User>) => {
    try {
      const response = await fetch(adminApi(`/admin/users/${userId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(adminApi(`/admin/users/${userId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete user');
      }
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const exportCustomerData = async () => {
    try {
      const response = await fetch(adminApi('/admin/export-customers'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'customers_export.json';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to export customer data');
    }
  };

  const importCustomerData = async () => {
    try {
      const response = await fetch(adminApi('/admin/import-customers'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ json_data: importData })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Import completed: ${result.imported} imported, ${result.updated} updated, ${result.errors.length} errors`);
        setImportDialogOpen(false);
        setImportData('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to import customer data');
      }
    } catch (err) {
      setError('Failed to import customer data');
    }
  };

  const downloadAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);

      const response = await fetch(`${adminApi('/admin/audit-logs/download')}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'audit_logs.json';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to download audit logs');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'critical': return <Error />;
      default: return <SystemUpdate />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <AdminPanelSettings sx={{ mr: 2, verticalAlign: 'middle' }} />
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Comprehensive system management and monitoring
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="System Overview" icon={<Assessment />} />
        <Tab label="User Management" icon={<People />} />
        <Tab label="Audit Logs" icon={<History />} />
        <Tab label="System Configuration" icon={<Settings />} />
        <Tab label="Data Management" icon={<Download />} />
      </Tabs>

      {/* System Overview Tab */}
      {activeTab === 0 && systemOverview && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Users</Typography>
                  <Typography variant="h4">{systemOverview.users.total}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemOverview.users.active} active, {systemOverview.users.admins} admins
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Customers</Typography>
                  <Typography variant="h4">{systemOverview.customers.total}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemOverview.customers.active} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Alerts</Typography>
                  <Typography variant="h4">{systemOverview.alerts.active}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemOverview.alerts.recent} recent
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Check-ins</Typography>
                  <Typography variant="h4">{systemOverview.checkins.success_rate.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Success rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {systemHealth && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>System Health</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Box textAlign="center">
                      <Chip
                        icon={getStatusIcon(systemHealth.system_status)}
                        label={systemHealth.system_status.toUpperCase()}
                        color={getStatusColor(systemHealth.system_status)}
                        size="medium"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">CPU Usage</Typography>
                    <Typography variant="h6">{systemHealth.cpu_usage_percent}%</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">Memory Usage</Typography>
                    <Typography variant="h6">{systemHealth.memory_usage_percent}%</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">Database Size</Typography>
                    <Typography variant="h6">{systemHealth.database_size_mb.toFixed(1)} MB</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* User Management Tab */}
      {activeTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">User Management</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setUserDialogOpen(true)}
            >
              Add User
            </Button>
          </Box>

          {orphanedUsers.length > 0 && (
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Incomplete Signups ({orphanedUsers.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These accounts were created during signup but never finished payment / customer
                  assignment. Delete them to free the email and phone for another attempt.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orphanedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.full_name || 'N/A'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phone_number || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={user.is_active ? 'Active (orphan)' : 'Inactive (orphan)'}
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {user.created_at
                              ? new Date(user.created_at).toLocaleDateString()
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              disabled={purgingOrphanId === user.id}
                              onClick={() => purgeOrphanedUser(user.id, user.email)}
                            >
                              {purgingOrphanId === user.id ? 'Deleting…' : 'Delete'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Active Users
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name || 'N/A'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingUser(user);
                              setUserForm({
                                email: user.email,
                                password: '',
                                full_name: user.full_name || '',
                                role: user.role,
                                is_active: user.is_active
                              });
                              setUserDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteUser(user.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">Audit Logs</Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadAuditLogs}
            >
              Download Logs
            </Button>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Filters</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Action"
                    value={auditFilters.action}
                    onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Resource Type"
                    value={auditFilters.resource_type}
                    onChange={(e) => setAuditFilters(prev => ({ ...prev, resource_type: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={auditFilters.start_date}
                    onChange={(e) => setAuditFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={auditFilters.end_date}
                    onChange={(e) => setAuditFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={fetchAuditLogs}>
                  Apply Filters
                </Button>
              </Box>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>{log.user_email || 'System'}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.resource_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.success ? 'Success' : 'Failed'}
                        color={log.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={JSON.stringify(log.details, null, 2)}>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* System Configuration Tab */}
      {activeTab === 3 && systemConfig && (
        <Box>
          <Typography variant="h6" gutterBottom>System Configuration</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Database</Typography>
                  <Typography variant="body2">Type: {systemConfig.database.type}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    URL: {systemConfig.database.url ? 'Configured' : 'Not configured'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Security</Typography>
                  <Typography variant="body2">
                    Token Expiry: {systemConfig.security.access_token_expire_minutes} minutes
                  </Typography>
                  <Typography variant="body2">
                    Algorithm: {systemConfig.security.algorithm}
                  </Typography>
                  <Chip
                    label={systemConfig.security.secret_key_configured ? 'Secret Key: OK' : 'Secret Key: Missing'}
                    color={systemConfig.security.secret_key_configured ? 'success' : 'error'}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Plivo SMS</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Chip
                      label={`Auth ID: ${systemConfig.plivo.auth_id_configured ? 'OK' : 'Missing'}`}
                      color={systemConfig.plivo.auth_id_configured ? 'success' : 'error'}
                      size="small"
                    />
                    <Chip
                      label={`Auth Token: ${systemConfig.plivo.auth_token_configured ? 'OK' : 'Missing'}`}
                      color={systemConfig.plivo.auth_token_configured ? 'success' : 'error'}
                      size="small"
                    />
                    <Chip
                      label={`Phone Number: ${systemConfig.plivo.phone_number_configured ? 'OK' : 'Missing'}`}
                      color={systemConfig.plivo.phone_number_configured ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Stripe Payments</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Chip
                      label={`Secret Key: ${systemConfig.stripe.secret_key_configured ? 'OK' : 'Missing'}`}
                      color={systemConfig.stripe.secret_key_configured ? 'success' : 'error'}
                      size="small"
                    />
                    <Chip
                      label={`Publishable Key: ${systemConfig.stripe.publishable_key_configured ? 'OK' : 'Missing'}`}
                      color={systemConfig.stripe.publishable_key_configured ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Data Management Tab */}
      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>Data Management</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Export Data</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Export customer data in JSON format
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={exportCustomerData}
                  >
                    Export Customers
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Import Data</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Import customer data from JSON format
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Upload />}
                    onClick={() => setImportDialogOpen(true)}
                  >
                    Import Customers
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              value={userForm.email}
              onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            {!editingUser && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              label="Full Name"
              value={userForm.full_name}
              onChange={(e) => setUserForm(prev => ({ ...prev, full_name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                label="Role"
                onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={userForm.is_active}
                  onChange={(e) => setUserForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingUser ? () => updateUser(editingUser.id, userForm) : createUser}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Customer Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste JSON data to import customers
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            label="JSON Data"
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={importCustomerData}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
